import { CategoryGroup } from 'app/modules/shared/interfaces/card-data';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';

export interface ICommonChartParameters {
    xAxisText: string;
    yAxisText: string;
    initialSeriesData: any;
    showLegend: boolean;
    secondaryTitle?: string;
    legendAlign?: 'left' | 'right' | 'center';
    route?: string;
    urlSegment?: string;
    compareTo?: string;
    lineSeriesTitle?: string;
    categoryGroup?: string[] | CategoryGroup[];
    purpose?: string;
    loanType?: string;
    isInverse?: boolean;
    height?: number;
    spacingBottom?: number;
    showDataLabels?: boolean;
    xAxisType?: string;
    addSecondaryTooltipData?: boolean;
    //primaryTooltipProperty?: string;
    secondaryTooltipProperty?: string;
    isSackChart?: boolean;
    tooltipitleSuffix?: string;
    chartTooltipParams?: object;
    showTooltipSummary?: boolean;
    xAxisQueryParams?: object;
    yAxisQueryParams?: object;
    chartQueryParams?: object;
    reportTitle?: string;
    //pointWidth?: number;
    // TODO: create a separate objects to tooltips and queryparams
}
export class CommonStackAndBarChartOptions {
    constructor(
        private reusableFunctions: ReusableFunctions,
        private userAccessService: UserAccessService,
        private navigationHelpService: NavigationHelpService
    ) {}

    public createNewStackedChart(properties: ICommonChartParameters): Highcharts.Options {
        const _this = this;
        properties.xAxisType = properties.xAxisType ?? 'category';
        properties.isInverse = properties.isInverse ?? false;
        properties.height = properties.height ?? 330;
        properties.spacingBottom = properties.spacingBottom ?? 0;
        properties.showDataLabels = properties.showDataLabels ?? false;
        properties.tooltipitleSuffix = properties.tooltipitleSuffix ?? '';
        properties.reportTitle = properties.reportTitle ?? '';
        let routerUrl = properties.route + '/' + properties.urlSegment;
        let clickablePoints = this.userAccessService.hasAccessToPage(routerUrl);
        return {
            chart: {
                spacingBottom: properties.spacingBottom,
                spacingLeft: 0,
                marginRight: 0,
                spacingRight: 0,
                spacingTop: 5,
                height: properties.height,
                events: {
                    load: function () {
                        if (clickablePoints) {
                            var xAxis = this.xAxis[0];
                            var xTicks = xAxis.ticks;
                            var yAxis = this.yAxis[0];
                            var points = this.series[0].points;
                            points.forEach((pointInf, i) => {
                                if (xTicks[i]) {
                                    var label = xTicks[i].label.element;
                                    label.style.cursor = 'pointer';
                                    label.onclick = function (item) {
                                        // TODO: Make query params dynamic
                                        // The following parameters are unique to a eClose ROI chart
                                        let queryParams = {
                                            selectedPeriod: pointInf.properties.period,
                                            titleSuffix:
                                                properties.reportTitle + ': ' + pointInf.name,
                                            eCloseOptionKey: properties.isSackChart
                                                ? 0
                                                : pointInf.properties.eCloseOptionKey,
                                        };

                                        _this.navigationHelpService.navigateTo(
                                            [routerUrl],
                                            queryParams,
                                            true
                                        );
                                    };
                                }
                            });
                        }
                    },
                },
            },
            title: {
                text: '',
            },
            credits: {
                enabled: false,
            },
            series: properties.initialSeriesData,
            exporting: {
                enabled: false,
                buttons: {
                    contextButton: {},
                },
            },
            xAxis: [
                {
                    type: properties.xAxisType,
                    categories: properties.categoryGroup,
                    tickInterval: 1,
                    startOnTick: false,
                    pointIntervalUnit: properties.xAxisText,
                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    title: {
                        text: properties.xAxisText,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                } as Highcharts.XAxisOptions,
            ],
            yAxis: [
                {
                    title: {
                        text: properties.yAxisText,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },

                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    lineWidth: 1,
                } as Highcharts.YAxisOptions,
            ],
            legend: {
                enabled: properties.showLegend,
                align: properties.legendAlign,
                verticalAlign: 'top',
            },
            plotOptions: {
                column: {
                    stacking: properties.isSackChart ? 'normal' : null,
                    minPointLength: 3,
                    states: {
                        hover: {
                            brightness: -0.1,
                        },
                    },
                },
                bar: {
                    states: {
                        hover: {
                            brightness: -0.1,
                        },
                    },
                },
                series: {
                    groupPadding: 0,
                    lineWidth: 3,
                    gridLineWidth: 0,
                    minorGridLineWidth: 0,
                    dataGrouping: {
                        enabled: false,
                    },
                    maxPointWidth: 50,
                    dataLabels: {
                        enabled: properties.showDataLabels,
                        inside: false,
                        formatter: function (label) {
                            return this.point.properties.dataLabel;
                        },
                    },
                    marker: {
                        enabled: false,
                        states: {
                            hover: {
                                radius: 4,
                                enabled: true,
                                radiusPlus: 0,
                                lineWidthPlus: 0,
                                lineWidth: 0,
                            },
                        },
                    },
                    point: {
                        events: {
                            click: function () {
                                if (clickablePoints) {
                                    // TODO: Make queary params dynamic
                                    let queryParams = {
                                        selectedPeriod: this.properties.period,
                                        titleSuffix:
                                            properties.reportTitle +
                                            ': ' +
                                            (properties.isSackChart
                                                ? this.series.name + ', ' + this.name
                                                : this.name),
                                        eCloseOptionKey: this.properties.eCloseOptionKey,
                                    };

                                    _this.navigationHelpService.navigateTo(
                                        [properties.route, properties.urlSegment],
                                        queryParams,
                                        true
                                    );
                                }
                            },
                        },
                    },
                    cursor: clickablePoints ? 'pointer' : 'default',
                } as Highcharts.PlotSeriesOptions,
            },
            tooltip: {
                style: {
                    color: '#223540',
                    fontFamily: 'Roboto',
                    fontSize: '12',
                    height: 12,
                },
                borderColor: 'white',
                backgroundColor: 'white',
                borderRadius: 6,
                useHTML: true,
                shared: true,
                //TODO: Make tooltip dynamic
                formatter: function (point) {
                    let length = this.points.length;
                    let thisPeriodTotal = 0;
                    let thisPeriodUnitsTotal = 0;
                    let toolTipContainer = '';
                    let mainValuePrecision = 0;
                    let mainValueType = 'currency';
                    let titlePrefix = '';
                    let titleValue = '';
                    let summaryLabel = 'Total savings';
                    let yValue = this.y;
                    let xValue = this.points[0].point.properties.units;

                    if (yValue || yValue === 0) {
                        let value = _this.reusableFunctions.formatNumber(
                            yValue,
                            mainValuePrecision,
                            mainValueType
                        );
                        let loanCount = _this.reusableFunctions.formatNumber(
                            xValue,
                            mainValuePrecision
                        );

                        let loanCountUnits = ' Units';
                        let tooltipTitle = '';
                        let tooltipTable = '';

                        if (properties.initialSeriesData[0].type === 'column') {
                            tooltipTable =
                                '<table style="border-collapse: collapse">' +
                                this.points.reduce(function (s, point, index) {
                                    let toolText = '';
                                    let value = _this.reusableFunctions.formatNumber(
                                        point.point.y,
                                        0,
                                        'currency'
                                    );
                                    let loanCount = _this.reusableFunctions.formatNumber(
                                        point.point.properties.units,
                                        0
                                    );
                                    let seriesName = point.series.name;
                                    if (index === 0) {
                                        titleValue = point.point.name.toUpperCase();
                                    }

                                    thisPeriodUnitsTotal += point.point.properties.units;
                                    thisPeriodTotal += point.point.y;
                                    toolText =
                                        s +
                                        `<tr style="line-height: 16px;">
                                            <td>
                                                <div style="color: ${
                                                    point.point.color
                                                }; font-size: 30px;">•</div>
                                            </td>
                                            <td><div style="padding: 2px 20px 2px 10px"><b>${seriesName}</b></div>
                                            </td>
                                            <td style="text-align: right"> ${value}</td>
                                            <td style="text-align: center; width: 30px;">  |  </td>
                                            <td style="text-align: right"> ${
                                                loanCount + loanCountUnits
                                            }</td>
                                         </tr>`;
                                    if (index === length - 1 && properties.showTooltipSummary) {
                                        toolText =
                                            toolText +
                                            `<tr style="border-top: 1px solid #b3b3b3; line-height: 16px;">
                                                <td></td>
                                                <td><b>${summaryLabel}</b></td>
                                                <td style="text-align: right;"><b>
                                                ${_this.reusableFunctions.formatNumber(
                                                    thisPeriodTotal,
                                                    0,
                                                    'currency'
                                                )}</b></td>
                                                <td style="text-align: center; width: 30px;">  |  </td>
                                                <td style="text-align: right;"><b>
                                                ${_this.reusableFunctions.formatNumber(
                                                    thisPeriodUnitsTotal,
                                                    0
                                                )} Units</b></td>
                                            </tr>`;
                                    }

                                    return toolText;
                                }, '') +
                                '</table>';
                        } else {
                            tooltipTable = `
                            <table>
                              <tr style="padding-bottom: 15px">
                                <td style="padding:0 15px 0 0;">${this.points[0].point.name}:</td>
                                <td style="text-align: right;">${value}</td>
                                <td style="text-align: center; width: 30px;">  |  </td>
                                <td style="text-align: right"> ${loanCount + loanCountUnits}</td>
                              </tr>
                             </table>`;
                        }
                        tooltipTitle =
                            titlePrefix + titleValue + ' ' + properties.tooltipitleSuffix;
                        toolTipContainer = `<div style="padding: 5px 5px 12px 12px; min-width: 220px">
                              <div style="border-bottom: 1px solid #edf0f4">
                                  <div style="font-size: 15px; font-weight: 700;">
                                    ${tooltipTitle}</div>
                              </div>
                              ${tooltipTable}
                          </div>`;
                    } else return null;
                    return toolTipContainer;
                },
            } as Highcharts.TooltipOptions,
            lang: {
                noData: 'There are no data results.',
            },
            noData: {
                style: {
                    fontWeight: 'bold',
                    fontSize: '15px',
                    color: '#303030',
                },
            },
        };
    }
}
